import { Box } from '@mui/material'
import { keyframes } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { MOODS, moodLook } from './moods.js'

// The Joulelingo character: the speech-bolt mark come to life. The green bubble is the body and
// the amber bolt its antenna, so it ties straight back to the logo (components/BrandMark.jsx).
// Flat shapes in fixed brand colours, drawn on a 120×130 grid, animated with plain CSS.
//
// Joulelingo's own pages only: it never appears on a partner's site or on the adult admin
// screens, where the brand's calm side applies.

const INK = '#13233A'
const GREEN = '#0F7B6C'
const BRIGHT = '#19A68D'
const BRIGHT_HI = '#4CC3AC'
const AMBER = '#FFB020'
const AMBER_DK = '#E08E00'
const CHEEK = '#FF8A7A'
const SKY = '#2F7DE1'
const BERRY = '#D9467A'
const GRAPE = '#7A55D6'

const ANTENNA = 'M66 4 L50 26 H60 L55 40 L74 16 H63 L70 4 Z'
const TAIL = 'M36 92 L28 118 Q27 122 31 119 L58 98 Z'
const SHADE =
    'M16 76 Q16 102 44 102 H76 Q104 102 104 76 Q86 92 60 92 Q34 92 16 76 Z'

const EYE_X = [44, 76]
const EYE_Y = 62

const ARMS = {
    rest: ['M18 74 Q8 80 10 92', 'M102 74 Q112 80 110 92'],
    up: ['M18 64 Q6 50 10 34', 'M102 64 Q114 50 110 34'],
    wave: ['M18 74 Q8 80 10 92', 'M102 70 Q118 58 114 40'],
    chin: ['M18 74 Q8 80 10 92', 'M102 80 Q100 96 82 94'],
    hug: ['M18 74 Q12 86 30 92', 'M102 74 Q108 86 90 92']
}

const stroke = (width = 4.5) => ({
    fill: 'none',
    stroke: INK,
    strokeWidth: width,
    strokeLinecap: 'round'
})

const OpenEyes = ({ lookUp = false }) =>
    EYE_X.map((x) => (
        <g key={x}>
            <circle cx={x} cy={EYE_Y} r="12" fill="#FFFFFF" />
            <circle
                cx={lookUp ? x + 3 : x + 1}
                cy={lookUp ? EYE_Y - 5 : EYE_Y + 1}
                r="5.5"
                fill={INK}
            />
            {!lookUp && (
                <circle cx={x + 3} cy={EYE_Y - 1} r="1.8" fill="#FFFFFF" />
            )}
        </g>
    ))

const Eyes = ({ kind }) => {
    if (kind === 'up') return <OpenEyes lookUp />
    if (kind === 'happy' || kind === 'sleep') {
        // Happy eyes arch up; sleeping eyes droop down.
        const bend = kind === 'happy' ? -9 : 6
        const lift = kind === 'happy' ? 2 : 0
        const half = kind === 'happy' ? 10 : 9
        return EYE_X.map((x) => (
            <path
                key={x}
                d={`M${x - half} ${EYE_Y + lift} Q${x} ${EYE_Y + bend} ${x + half} ${EYE_Y + lift}`}
                {...stroke()}
            />
        ))
    }
    if (kind === 'worried') {
        const [l, r] = EYE_X
        return (
            <>
                <OpenEyes />
                <path
                    d={`M${l - 10} ${EYE_Y - 15} L${l + 8} ${EYE_Y - 20}`}
                    {...stroke(4)}
                />
                <path
                    d={`M${r - 8} ${EYE_Y - 20} L${r + 10} ${EYE_Y - 15}`}
                    {...stroke(4)}
                />
            </>
        )
    }
    return <OpenEyes />
}

const Mouth = ({ kind }) => {
    switch (kind) {
        case 'open':
            return (
                <>
                    <path d="M46 78 Q60 102 74 78 Z" fill={INK} />
                    <ellipse cx="60" cy="90" rx="6" ry="3.5" fill={CHEEK} />
                </>
            )
        case 'o':
            return <ellipse cx="60" cy="83" rx="4" ry="5" fill={INK} />
        case 'hmm':
            return <path d="M52 84 L69 81" {...stroke()} />
        case 'wobble':
            return <path d="M48 84 q4 -5 8 0 t8 0 t8 0" {...stroke(4)} />
        default:
            return <path d="M48 80 Q60 92 72 80" {...stroke()} />
    }
}

const CONFETTI = [
    [4, 14, AMBER, 20],
    [22, 4, BERRY, -15],
    [96, 6, SKY, 30],
    [112, 20, GRAPE, -25],
    [0, 40, SKY, 45],
    [114, 44, AMBER, 10],
    [40, 0, GRAPE, 60],
    [80, -2, BRIGHT, -40]
]

const Extra = ({ kind }) => {
    switch (kind) {
        case 'confetti':
            return (
                <g className="jl-confetti">
                    {CONFETTI.map(([x, y, colour, turn]) => (
                        <rect
                            key={`${x}-${y}`}
                            x={x}
                            y={y}
                            width="7"
                            height="11"
                            rx="2"
                            fill={colour}
                            transform={`rotate(${turn} ${x + 3} ${y + 5})`}
                        />
                    ))}
                </g>
            )
        case 'question':
            return (
                <text
                    className="jl-pop"
                    x="100"
                    y="30"
                    fill={AMBER_DK}
                    fontFamily="'Bricolage Grotesque Variable', sans-serif"
                    fontSize="30"
                    fontWeight="800"
                >
                    ?
                </text>
            )
        case 'zzz':
            return (
                <g
                    className="jl-zzz"
                    fill={SKY}
                    fontFamily="'Bricolage Grotesque Variable', sans-serif"
                    fontWeight="800"
                >
                    <text x="98" y="34" fontSize="18">
                        z
                    </text>
                    <text x="108" y="18" fontSize="13">
                        z
                    </text>
                </g>
            )
        case 'sweat':
            return (
                <path
                    className="jl-drip"
                    d="M100 40 Q94 50 100 54 Q106 50 100 40 Z"
                    fill={SKY}
                    opacity="0.85"
                />
            )
        default:
            return null
    }
}

const bob = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}`
const jump = keyframes`0%,100%{transform:translateY(0) scale(1)}30%{transform:translateY(-16px) scale(1.04,0.96)}60%{transform:translateY(0) scale(1.06,0.92)}`
const wiggle = keyframes`0%,100%{transform:rotate(0)}25%{transform:rotate(-5deg)}75%{transform:rotate(5deg)}`
const breathe = keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.03,0.97)}`
const tilt = keyframes`0%,100%{transform:rotate(0)}50%{transform:rotate(-6deg)}`
const wave = keyframes`0%,100%{transform:rotate(0)}25%{transform:rotate(-20deg)}75%{transform:rotate(12deg)}`
const zzz = keyframes`0%{opacity:0;transform:translate(0,6px)}50%{opacity:1}100%{opacity:0;transform:translate(6px,-10px)}`
const pop = keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.25)}`
const confetti = keyframes`0%{transform:translateY(-8px);opacity:0}25%{opacity:1}100%{transform:translateY(18px);opacity:0}`
const drip = keyframes`0%{transform:translateY(0);opacity:0}30%{opacity:0.9}100%{transform:translateY(12px);opacity:0}`

const MOTIONS = {
    bob: `${bob} 2.6s ease-in-out infinite`,
    jump: `${jump} 1.1s ease-in-out infinite`,
    wiggle: `${wiggle} 0.9s ease-in-out infinite`,
    breathe: `${breathe} 3.2s ease-in-out infinite`,
    tilt: `${tilt} 2.8s ease-in-out infinite`
}

// The whole character moves with its mood; the extras and the waving arm move on their own.
// Nothing moves for anyone who has asked their device for less motion.
const motionSx = (motion) => ({
    display: 'inline-flex',
    lineHeight: 0,
    transformOrigin: '50% 100%',
    animation: MOTIONS[motion],
    '& .jl-wave': {
        transformBox: 'fill-box',
        transformOrigin: '0% 100%',
        animation: `${wave} 1.4s ease-in-out infinite`
    },
    '& .jl-zzz, & .jl-pop, & .jl-confetti, & .jl-drip': {
        transformBox: 'fill-box',
        transformOrigin: 'center'
    },
    '& .jl-zzz': { animation: `${zzz} 2.4s ease-in-out infinite` },
    '& .jl-pop': { animation: `${pop} 1.3s ease-in-out infinite` },
    '& .jl-confetti': { animation: `${confetti} 1.2s ease-in infinite` },
    '& .jl-drip': { animation: `${drip} 1.6s ease-in infinite` },
    '@media (prefers-reduced-motion: reduce)': {
        '&, & *': { animation: 'none !important' }
    }
})

const STILL_SX = { display: 'inline-flex', lineHeight: 0 }

const JoulelingoCharacter = ({
    mood = 'happy',
    size = 160,
    label,
    animated = true
}) => {
    const look = moodLook(mood)
    const [leftArm, rightArm] = ARMS[look.arms]
    const arm = {
        fill: 'none',
        stroke: BRIGHT,
        strokeWidth: 10,
        strokeLinecap: 'round'
    }
    // Decorative by default: most places it sits beside words that already say what it means.
    const a11y = label
        ? { role: 'img', 'aria-label': label }
        : { 'aria-hidden': true }

    return (
        <Box
            component="span"
            data-character-mood={mood in MOODS ? mood : 'happy'}
            data-character-motion={animated ? look.motion : 'still'}
            sx={animated ? motionSx(look.motion) : STILL_SX}
        >
            <svg
                height={Math.round((size * 130) / 120)}
                style={{ overflow: 'visible' }}
                viewBox="0 0 120 130"
                width={size}
                {...a11y}
            >
                <path d={leftArm} {...arm} />
                <path
                    className={look.arms === 'wave' ? 'jl-wave' : undefined}
                    d={rightArm}
                    {...arm}
                />
                <path d={TAIL} fill={BRIGHT} />
                <rect
                    x="16"
                    y="34"
                    width="88"
                    height="68"
                    rx="28"
                    fill={BRIGHT}
                />
                <path d={SHADE} fill={GREEN} opacity="0.35" />
                <ellipse
                    cx="34"
                    cy="46"
                    rx="9"
                    ry="5"
                    fill={BRIGHT_HI}
                    opacity="0.8"
                />
                <path
                    d={ANTENNA}
                    fill={AMBER}
                    stroke={AMBER_DK}
                    strokeLinejoin="round"
                    strokeWidth="2"
                />
                <Eyes kind={look.eyes} />
                {[34, 86].map((x) => (
                    <ellipse
                        key={x}
                        cx={x}
                        cy="76"
                        rx="6"
                        ry="3.8"
                        fill={CHEEK}
                        opacity="0.6"
                    />
                ))}
                <Mouth kind={look.mouth} />
                <Extra kind={look.extra} />
            </svg>
        </Box>
    )
}

JoulelingoCharacter.propTypes = {
    mood: PropTypes.oneOf(Object.keys(MOODS)),
    size: PropTypes.number,
    // Say what the character is doing when it carries meaning on its own; leave it out when the
    // words beside it already do, and screen readers skip it.
    label: PropTypes.string,
    // Off for a still picture, e.g. in a list of many.
    animated: PropTypes.bool
}

export default JoulelingoCharacter
